import { useEffect, useState } from 'react';
import { isPrerender } from './default-brand.js';
import { logger } from './logger.js';

/**
 * How long the free trial actually is (gap G28).
 *
 * The number was baked into ten marketing strings across four locales while
 * `platformConfig/moneyPolicy.trial.days` had been owner-editable since ADR-0033.
 * So changing the trial in the admin panel changed what a new tenant got and
 * left the public promise saying something else — a page advertising 14 days
 * while the product grants 30 is worse than either number on its own.
 *
 * Read once, not live. This is a static marketing page, the value changes about
 * never, and a listener per visitor on a public site is a real cost against the
 * ~$0-idle rule for no benefit.
 *
 * The seeded default is the fallback, deliberately. Prerender runs with no
 * Firestore, so the HTML a crawler sees carries this number; if the owner ever
 * changes the trial, the prerendered copy is briefly stale until the next
 * deploy while the hydrated page is correct. Showing the shipped default beats
 * showing a gap or a spinner where a number should be.
 *
 * The repository is imported INSIDE the effect, not at module scope. Six routes
 * call this hook — including the home page — so a static import put
 * `money-policy-repo` → `_base` → the Firestore SDK into the marketing ENTRY
 * chunk, and Vite then emitted a `<link rel="modulepreload">` for it. Measured
 * 2026-08-23: 346 KB of Firebase fetched at the highest priority on first paint
 * so that a headline could say "14 days" — a number this file already knows and
 * renders immediately. Same defect as the 1.25 MB of locale strings removed in
 * PR #226 and the brand-resolver import in `use-platform-brand.tsx`: work that
 * only ever runs after paint had been hoisted in front of it.
 */
export const DEFAULT_TRIAL_DAYS = 14;

export function useTrialDays(): number {
  const [days, setDays] = useState(DEFAULT_TRIAL_DAYS);

  useEffect(() => {
    // Prerender: the default below is already the answer — never touch Firestore.
    //
    // This guard is what actually keeps Firebase off the critical path, and the
    // dynamic import alone does NOT. The prerenderer drives a real browser, so
    // the effect runs, the dynamic import resolves, and Vite's runtime preload
    // helper injects `<link rel="modulepreload">` into `<head>` — which the
    // snapshot then bakes into the served HTML. Measured 2026-08-23: deferring
    // the import moved Firebase from all 104 prerendered pages onto exactly the
    // 10 that call this hook, which is not a fix. Skipping the read entirely is.
    if (isPrerender()) return;

    let cancelled = false;
    void (async () => {
      try {
        // Through the repository, not the SDK: the schema is validated on read,
        // so a malformed policy document cannot put a nonsense number on a
        // public page.
        const { moneyPolicyRepo } = await import('./repositories/money-policy-repo.js');
        const policy = await moneyPolicyRepo.get('moneyPolicy');
        const value = policy?.trial?.days;
        // A zero is meaningful — it means "no trial" — so this checks the type
        // rather than truthiness, which would silently fall back to 14.
        if (!cancelled && typeof value === 'number' && Number.isFinite(value) && value >= 0) {
          setDays(Math.trunc(value));
        }
      } catch (e) {
        // A public page must never break because a config read failed; the
        // shipped default is a correct-enough answer.
        logger.warn('trial length unavailable; using the shipped default', { error: String(e) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return days;
}
